import {useEffect, useRef, useState} from 'react';
import {useParams as useRouteParams} from 'react-router-dom';
import {fetchCompositionReviewStatusService} from 'store/services/compositionServices';
import {CompositionReviewedStatus} from 'utils/types/newReviewed';
import NewReviewed from 'containers/NewReviewed';
import SimplifiedFeedback from 'containers/SimplifiedFeedback';
import FeedbackLoadingView from 'containers/SimplifiedFeedback/components/FeedbackLoadingView';

// Routes between the simplified feedback and the full one, per composition. Reads
// `feedback_layout` (computed by the backend directly from the student's grade,
// `Composition.uses_simplified_feedback`) — not `follows_reading_cycle`, which is only true when
// the composition is linked to the Ciclo template's learning_path_instance, and was therefore
// false for real 5th-grade students without that link (found live, 2026-09-22: it fell through to
// the full feedback instead of the simplified one).
function DevolutivaGate(): JSX.Element {
  const {composition_id} = useRouteParams();
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<CompositionReviewedStatus>();
  const isMountedRef = useRef(true);
  const runIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    // A run id identifies this effect run: a request started for a previous composition_id can
    // still resolve after the effect re-runs, so compare against the current run before applying
    // its result — the mount ref alone does not catch a composition change while still mounted.
    runIdRef.current += 1;
    const runId = runIdRef.current;
    setIsLoading(true);
    setStatus(undefined);

    fetchCompositionReviewStatusService(composition_id)
      .then(({data}) => {
        if (!isMountedRef.current || runId !== runIdRef.current) return;
        setStatus(data);
        setIsLoading(false);
      })
      .catch(() => {
        // On failure, falls back to the default feedback (conservative behavior).
        if (isMountedRef.current && runId === runIdRef.current)
          setIsLoading(false);
      });

    return () => {
      isMountedRef.current = false;
    };
  }, [composition_id]);

  if (isLoading) {
    return <FeedbackLoadingView />;
  }

  const usesSimplifiedFeedback = status?.feedback_layout === 'simplified';

  if (usesSimplifiedFeedback) {
    return <SimplifiedFeedback />;
  }

  return <NewReviewed />;
}

export default DevolutivaGate;
